/**
 * WebAuthn browser glue.
 *
 * The backend speaks base64url JSON (the `PublicKeyCredential*Options`
 * shapes with challenge / id fields encoded as base64url strings), while
 * `navigator.credentials.create()` / `.get()` need real ArrayBuffers, and
 * the serialized credential they return must go back to the backend as
 * base64url again. These helpers convert in both directions.
 *
 * base64url — NOT base64: '+'→'-', '/'→'_', no '=' padding. Getting this
 * wrong silently corrupts the challenge and every ceremony fails, so the
 * conversion is centralised here.
 */

/** ArrayBuffer / typed array → base64url string (no padding). */
export function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCodePoint(b);
  }
  // base64 emits `=` only as trailing padding, so a single-char global strip is
  // equivalent to trimming the `=+$` tail — and avoids the backtracking regex
  // Sonar flags as a ReDoS risk (S5852).
  return globalThis
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/** base64url string → ArrayBuffer. Tolerates missing padding. */
export function base64urlToBuffer(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '=',
  );
  const binary = globalThis.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.codePointAt(i) ?? 0;
  }
  return bytes.buffer;
}

// ── Backend JSON shapes (base64url strings for binary fields) ──────────

interface CredentialDescriptorJSON {
  type: PublicKeyCredentialType;
  id: string;
  transports?: AuthenticatorTransport[];
}

interface CreationOptionsJSON {
  challenge: string;
  user: { id: string; name: string; displayName: string };
  rp: PublicKeyCredentialRpEntity;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: CredentialDescriptorJSON[];
  extensions?: AuthenticationExtensionsClientInputs;
}

interface RequestOptionsJSON {
  challenge: string;
  timeout?: number;
  rpId?: string;
  userVerification?: UserVerificationRequirement;
  allowCredentials?: CredentialDescriptorJSON[];
  extensions?: AuthenticationExtensionsClientInputs;
}

function mapDescriptors(
  list: CredentialDescriptorJSON[] | undefined,
): PublicKeyCredentialDescriptor[] | undefined {
  if (!list) return undefined;
  return list.map((d) => ({
    type: d.type,
    id: base64urlToBuffer(d.id),
    ...(d.transports ? { transports: d.transports } : {}),
  }));
}

/** Convert backend registration-options JSON into the ArrayBuffer-carrying
 *  shape `navigator.credentials.create()` expects. */
export function preformatCreationOptions(
  json: CreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    user: {
      ...json.user,
      id: base64urlToBuffer(json.user.id),
    },
    excludeCredentials: mapDescriptors(json.excludeCredentials),
  };
}

/** Convert backend assertion-options JSON into the ArrayBuffer-carrying
 *  shape `navigator.credentials.get()` expects. */
export function preformatRequestOptions(
  json: RequestOptionsJSON,
): PublicKeyCredentialRequestOptions {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    allowCredentials: mapDescriptors(json.allowCredentials),
  };
}

/** Serialize a registration credential (attestation) back to base64url
 *  JSON for POST /webauthn/register/verify. */
export function serializeAttestation(
  credential: PublicKeyCredential,
): Record<string, unknown> {
  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      attestationObject: bufferToBase64url(response.attestationObject),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

/** Serialize an authentication credential (assertion) back to base64url
 *  JSON for POST /auth/mfa/verify (method: webauthn). */
export function serializeAssertion(
  credential: PublicKeyCredential,
): Record<string, unknown> {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      authenticatorData: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle
        ? bufferToBase64url(response.userHandle)
        : null,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}
